import type { PointerEvent } from 'react';

/**
 * How a field looks on an account detail — the parent's, the student's, a child's under its
 * parent — so every text field and dropdown there is the same size, rim and type, whichever pane
 * draws it. Each is a class string for the design system's own `Input`, `Textarea`, `Combobox`
 * or `Select`, which the detail sizes down from their defaults.
 */

/** A field's rim and type: a lighter rim than the kit's, so what is in it reads first. */
export const ACCOUNT_FIELD_SKIN =
  'border-foreground/15 shadow-none text-regular-s md:text-regular-s';

/**
 * A text field: 34px, the rim, 14px text — the kit's `Input` drops to 11px at `md`, so the `md:`
 * repeat in the skin is what keeps it.
 */
export const ACCOUNT_FIELD = `h-[34px] ${ACCOUNT_FIELD_SKIN}`;

/**
 * A dropdown beside the Info fields (`Combobox`, `SelectTrigger`): `ACCOUNT_FIELD`'s height, rim
 * and 14px text. The kit sizes both by `data-size`, so the height is set through it. A `Combobox`
 * takes no `flex-1`: its box sits in cmdk's column, where growing makes it taller, not wider.
 */
export const DROPDOWN_FIELD = `data-[size=default]:h-[34px] ${ACCOUNT_FIELD_SKIN}`;

/**
 * A `SelectContent`'s height: a long list scrolls inside 16rem rather than running down the page,
 * and never past the screen's edge. With `avoidCollisions={false}` it always opens below.
 */
export const DROPDOWN_LIST =
  'max-h-[min(16rem,var(--radix-select-content-available-height))]';

/** Below this much screen under a field, its list would open upwards. */
const LIST_ROOM = 280;

/**
 * Before a dropdown opens, scroll its field to the middle of the screen when the list would not
 * fit under it, so it drops down rather than flipping up over the field. For a row of dropdowns'
 * `onPointerDownCapture`.
 */
export function makeRoomBelow(event: PointerEvent<HTMLElement>) {
  const field = (event.target as HTMLElement).closest<HTMLElement>(
    '[data-slot=combobox], [data-slot=select-trigger], [data-slot=popover-trigger]',
  );
  if (!field) return;
  if (window.innerHeight - field.getBoundingClientRect().bottom < LIST_ROOM)
    field.scrollIntoView({ block: 'center' });
}

/**
 * A list's search box (the kit's `SearchField`), beside the list's dropdowns in `DROPDOWN_FIELD`:
 * their height and rim, at the dialog size's glyph and inset. Its words go in `LIST_SEARCH_INPUT`.
 */
export const LIST_SEARCH =
  'h-[34px] rounded-lg border-foreground/15 shadow-none';

/** A list search box's words: 14px, as in every field beside it. */
export const LIST_SEARCH_INPUT = 'text-regular-s leading-5';
